/**
 * AI personalities (ADR-0053).
 *
 * A personality is not a separate AI. It is a set of multipliers on the same weights
 * every AI civilisation scores its choices with (`rules.ai`): a militarist values
 * soldiers more and needs less of an edge before it goes to war; a scientist values
 * libraries more and peace sooner. The decisions are made by the same code, so the
 * differences between rivals are numbers anyone can read and tune in
 * `content/personalities.json`.
 */

import type { Content } from '../../../content/schema/index.js';
import type { Civ } from '../state/types.js';

export type AiWeights = Content['rules']['ai'];

/** Weights that are counts or turns, and so stay whole numbers after scaling. */
const WHOLE = new Set([
  'settleSearchRadius',
  'exploreSearchRadius',
  'garrisonPeace',
  'garrisonWar',
  'warMinimumUnits',
  'warEarliestTurn',
  'minimumWarTurns',
  'stalemateTurns',
  'peaceWearyCitizens',
  'warAims',
  'armyMinimum',
  'gatherRadius',
  'gatherMaxTurns',
  'marchSpread',
  'siegeUnits',
  'upgradeReserve',
  'upgradeSavingSteps',
  'threatRadius',
  'maxGarrison',
  'governmentMinTurns',
]);
/** Weights that are probabilities, and so stay within 0–1. */
const CHANCES = new Set([
  'warChancePerTurn',
  'attackMinimumOdds',
  'siegeOdds',
  'personalityTwist',
  'threatPeaceShare',
  'offerChance',
  'demandChance',
  'demandGoldShare',
]);

const cache = new WeakMap<Content, Map<number, AiWeights>>();

/** The weights a civilisation's AI plays by: the base rules, scaled by its personality. */
export function weightsFor(content: Content, civ: Civ | undefined): AiWeights {
  const id = civ?.personality ?? -1;
  let table = cache.get(content);
  if (!table) {
    table = new Map();
    cache.set(content, table);
  }
  const hit = table.get(id);
  if (hit) return hit;

  const base = content.rules.ai;
  const out: Record<string, unknown> = { ...base };
  if (id >= 0 && id < content.personalities.all.length) {
    for (const [key, factor] of Object.entries(content.personalities.get(id).weights)) {
      let value = (base as Record<string, number>)[key]! * factor;
      if (WHOLE.has(key)) value = Math.max(key === 'threatRadius' || key === 'maxGarrison' ? 1 : 0, Math.round(value));
      if (CHANCES.has(key)) value = Math.min(1, Math.max(0, value));
      if (key === 'warStrengthRatio') value = Math.max(1, value);
      out[key] = value;
    }
  }
  const weights = out as AiWeights;
  table.set(id, weights);
  return weights;
}
